import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../Icon";
import { WEBSITE_SERVICE } from "../content";
import { CardGrid, CtaBand, FaqList, SectionHead, useSeo } from "../ui";
import { contactLink } from "./content";
import { Lines } from "./LandingPage";
import { WEBSITE_LANDING as W, WEBSITE_SLUG } from "./website";
import { rememberSegment, trackVisit } from "./tracking";

/* =========================================================
   자영업 · 소상공인 홈페이지 랜딩 — /for/website
   핵심: 검색이 포털에서 AI 질문으로 바뀌었고, 그래서 블로그보다 내 홈페이지가 유리하다.
   흐름: AI에게 묻는 손님(히어로) → 포털 검색 vs AI 질문 → 아직도 블로그 광고비(공감)
        → 광고 vs 홈페이지 → 필요한 건 딱 이 정도 → 가격 → 진행 → FAQ → 문의
   ========================================================= */
export default function WebsiteLanding() {
  useSeo({ title: W.seo.title, description: W.seo.description, path: `/for/${WEBSITE_SLUG}` });

  useEffect(() => {
    trackVisit(WEBSITE_SLUG);
    rememberSegment(WEBSITE_SLUG);
  }, []);

  const toContact = contactLink(WEBSITE_SLUG);
  const { math } = W.compare;
  const { chat } = W.hero;

  return (
    <>
      {/* 1. 히어로 — 핵심: 검색이 AI 질문으로 바뀌었고, 그래서 내 홈페이지가 유리하다 */}
      <section className="lp-hero">
        <div className="lp-hero__inner wl-hero">
          <div className="wl-hero__copy">
            <div className="kicker">{W.hero.kicker}</div>
            <h1 className="lp-hero__title"><Lines text={W.hero.title} /></h1>
            <p className="lp-hero__lead">{W.hero.lead}</p>
            <p className="wl-hero__punch">{W.hero.punch}</p>
            <div className="lp-hero__actions">
              <Link to={toContact} className="btn btn--primary btn--lg">
                홈페이지 상담 문의하기
                <Icon.ArrowRight width="18" height="18" aria-hidden="true" focusable="false" />
              </Link>
            </div>
            <ul className="lp-hero__chips">
              {W.hero.chips.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </div>
          <figure className="wl-chat wl-hero__chat">
            <figcaption className="wl-chat__note">{chat.note}</figcaption>
            <p className="wl-chat__q">{chat.question}</p>
            <div className="wl-chat__a">
              <span className="wl-chat__who">AI</span>
              <p>{chat.answer}</p>
              <span className="wl-chat__src">
                <Icon.Globe width="13" height="13" aria-hidden="true" focusable="false" /> {chat.source}
              </span>
            </div>
          </figure>
        </div>
      </section>

      {/* 2. 포털 검색 → AI 질문 */}
      <section className="section">
        <SectionHead kicker="Search has changed" title={<Lines text={W.shift.title} />} />
        <div className="wl-flow">
          {[W.shift.before, W.shift.after].map((col, ci) => (
            <div key={col.label} className={`wl-flow__col ${ci ? "wl-flow__col--now" : ""}`}>
              <span className="wl-flow__label">{col.label}</span>
              <ol className="wl-flow__steps">
                {col.steps.map((st) => <li key={st}>{st}</li>)}
              </ol>
            </div>
          ))}
        </div>
        <div className="wl-key">
          <h3 className="wl-key__title"><Lines text={W.shift.key} /></h3>
          <ul className="wl-points">
            {W.shift.points.map((p) => (
              <li key={p.title}>
                <Icon.Check width="16" height="16" aria-hidden="true" focusable="false" />
                <div>
                  <strong>{p.title}</strong>
                  <span>{p.desc}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 3. 공감 — 아직도 블로그 광고비 */}
      <section className="section">
        <SectionHead kicker="Pain" title={<Lines text={W.painsTitle} />} />
        <ol className="lp-pains">
          {W.pains.map((p) => (
            <li key={p.title} className="lp-pain">
              <h3 className="lp-pain__title">{p.title}</h3>
              <p className="lp-pain__desc">{p.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* 4. 광고 vs 홈페이지 */}
      <section className="section">
        <SectionHead kicker="Compare" title={<Lines text={W.compare.title} />} />
        <div className="wl-compare" role="table" aria-label="블로그 광고와 홈페이지 비교">
          <div className="wl-compare__row wl-compare__row--head" role="row">
            <span role="columnheader"><span className="sr-only">항목</span></span>
            <span role="columnheader">{W.compare.cols[0]}</span>
            <span role="columnheader" className="wl-compare__mine">{W.compare.cols[1]}</span>
          </div>
          {W.compare.rows.map(([k, ad, mine]) => (
            <div key={k} className="wl-compare__row" role="row">
              <span role="rowheader" className="wl-compare__key">{k}</span>
              <span role="cell" className="wl-compare__ad">{ad}</span>
              <span role="cell" className="wl-compare__mine">{mine}</span>
            </div>
          ))}
        </div>
        <div className="wl-math">
          <div className="wl-math__side">
            <span className="wl-math__label">{math.label}</span>
            <strong className="wl-math__value wl-math__value--ad">{math.value}</strong>
          </div>
          <span className="wl-math__vs" aria-hidden="true">vs</span>
          <div className="wl-math__side">
            <span className="wl-math__label">{math.vs}</span>
            <strong className="wl-math__value">{math.vsValue}</strong>
          </div>
        </div>
      </section>

      {/* 5. 필요한 건 딱 이 정도 */}
      <section className="section">
        <SectionHead kicker="What you get" title={<Lines text={W.needs.title} />} desc={W.needs.desc} />
        <CardGrid items={W.needs.items} cols={2} />
        <p className="lp-note">
          필요하면 더할 수 있는 것 — {WEBSITE_SERVICE.extras.join(" · ")}
        </p>
      </section>

      {/* 6. 가격 */}
      <section className="section">
        <SectionHead kicker="Price" title={W.price.title} desc={W.price.desc} />
        <div className="wl-price">
          <div className="wl-price__tag">
            <strong>{WEBSITE_SERVICE.price}</strong>
            <span>{WEBSITE_SERVICE.priceNote}</span>
          </div>
          <ul className="lp-option__terms wl-price__points">
            {W.price.points.map((t) => (
              <li key={t}>
                <Icon.Check width="14" height="14" aria-hidden="true" focusable="false" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 7. 진행 과정 */}
      <section className="section">
        <SectionHead kicker="Process" title="사진과 메뉴만 보내 주세요" />
        <ol className="lp-steps wl-steps">
          {WEBSITE_SERVICE.flow.map((s, i) => (
            <li key={s} className="lp-step">
              <span className="lp-step__no">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="lp-step__title">{s}</h3>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* 8. FAQ */}
      <section className="section">
        <SectionHead kicker="FAQ" title="자주 묻는 질문" />
        <FaqList items={W.faq} />
      </section>

      {/* 9. 최종 CTA */}
      <CtaBand title={<Lines text={W.cta.title} />} desc={W.cta.desc} to={toContact} label="홈페이지 상담 문의" />
    </>
  );
}
